/**
 * Deployment Workflow — pure helpers (no React).
 *
 * Defines the 7 canonical stages, the risk-tier-specific paths, and the
 * functions that advance a request through the pipeline + append audit
 * entries. Unit-testable in isolation.
 *
 * Stages:
 *   1  Submitted              · request lodged, awaiting triage
 *   2  Under Review           · IT Security · feasibility + access scoping
 *   3  Governance Assessment  · Legal & Compliance · regulatory mapping
 *   4  Approval Required      · CAIO (+ Board Risk for high-risk)
 *   5  Approved               · sign-off captured, awaiting deployment slot
 *   6  Deployment Ready       · Engineering · build, configure, smoke-test
 *   7  Deployed               · live & under continuous governance
 *
 * Risk paths (low-risk skips Governance Assessment):
 *   low  → [1, 2,      4, 5, 6, 7]
 *   med  → [1, 2, 3,   4, 5, 6, 7]
 *   high → [1, 2, 3,   4, 5, 6, 7]   (stage 4 requires Board Risk)
 */

export const STAGES = [
  {
    id: 1,
    key: "submitted",
    label: "Submitted",
    description: "Request lodged with ALGOS; awaiting triage.",
    owner: "Submitter",
    badgeTone: "tralenex",
  },
  {
    id: 2,
    key: "under-review",
    label: "Under Review",
    description: "IT Security validates feasibility, data access, and vendor posture.",
    owner: "IT Security",
    badgeTone: "tralenex",
  },
  {
    id: 3,
    key: "governance",
    label: "Governance Assessment",
    description: "Legal & Compliance map the request to APRA CPS 234, ASIC RG 271, Privacy Act AU, ISO 42001.",
    owner: "Legal & Compliance",
    badgeTone: "opp",
  },
  {
    id: 4,
    key: "approval",
    label: "Approval Required",
    description: "CAIO sign-off captured. High-risk requests also require Board Risk Sub-Committee endorsement.",
    owner: "CAIO",
    badgeTone: "gold",
  },
  {
    id: 5,
    key: "approved",
    label: "Approved",
    description: "All sign-offs captured. Slot the work into the deployment calendar.",
    owner: "CAIO",
    badgeTone: "low",
  },
  {
    id: 6,
    key: "ready",
    label: "Deployment Ready",
    description: "Engineering builds, configures, observability hooked, smoke tests green.",
    owner: "Engineering",
    badgeTone: "tralenex",
  },
  {
    id: 7,
    key: "deployed",
    label: "Deployed",
    description: "Live and under continuous ALGOS governance.",
    owner: "Operations",
    badgeTone: "low",
  },
];

const PATH_LOW = [1, 2, 4, 5, 6, 7];
const PATH_MED = [1, 2, 3, 4, 5, 6, 7];
const PATH_HIGH = [1, 2, 3, 4, 5, 6, 7];

export function stageById(id) {
  return STAGES.find((s) => s.id === id);
}

export function pathFor(risk) {
  if (risk === "low") return PATH_LOW;
  if (risk === "high") return PATH_HIGH;
  return PATH_MED;
}

/** Approvers required to clear stage 4 for the given risk band. */
export function approversFor(risk) {
  if (risk === "high")
    return ["IT Security", "Legal & Compliance", "CAIO", "Board Risk Sub-Committee"];
  if (risk === "med") return ["IT Security", "Legal & Compliance", "CAIO"];
  return ["IT Security", "CAIO"];
}

/** Returns the next stage id along the path, or null if already at the end. */
export function nextStageId(currentStageId, risk) {
  const path = pathFor(risk);
  const idx = path.indexOf(currentStageId);
  if (idx < 0 || idx >= path.length - 1) return null;
  return path[idx + 1];
}

/** Returns the previous stage id, or null if at the start (used for "request changes"). */
export function previousStageId(currentStageId, risk) {
  const path = pathFor(risk);
  const idx = path.indexOf(currentStageId);
  if (idx <= 0) return null;
  return path[idx - 1];
}

/** True when the request is in a terminal state — no further actions. */
export function isTerminal(request) {
  return request.stage === 7 || request.terminal === true;
}

const tsIsoNow = (now = new Date()) => now.toISOString().replace("T", " ").slice(0, 19);

/**
 * Compute the immutable next state for a request after an actor takes a
 * decision. Returns { request, audit } where:
 *   - request is the new request object (do not mutate caller's input)
 *   - audit  is the audit-log entry to append
 *
 * Decisions:
 *   "approve"  → advance to next stage (or terminate at 7)
 *   "reject"   → mark terminal + status "rejected"
 *   "changes"  → step back one stage (max stage 1) with status "changes-requested"
 *   "deploy"   → only valid from stage 6 → 7
 */
export function applyDecision(request, decision, actor, now = new Date()) {
  const ts = tsIsoNow(now);
  const commonAudit = {
    ts,
    actor: actor?.id || actor?.name || "current-user",
    target: request.id,
  };

  if (decision === "reject") {
    const next = {
      ...request,
      terminal: true,
      status: "rejected",
      updatedAt: ts,
      decisions: [
        ...(request.decisions || []),
        { ts, actor: commonAudit.actor, decision: "rejected", stage: request.stage },
      ],
    };
    return { request: next, audit: { ...commonAudit, action: `deployment.rejected.stage${request.stage}` } };
  }

  if (decision === "changes") {
    const prev = previousStageId(request.stage, request.risk) || request.stage;
    const next = {
      ...request,
      stage: prev,
      status: "changes-requested",
      updatedAt: ts,
      decisions: [
        ...(request.decisions || []),
        { ts, actor: commonAudit.actor, decision: "changes-requested", stage: request.stage },
      ],
    };
    return {
      request: next,
      audit: { ...commonAudit, action: `deployment.changes.stage${request.stage}` },
    };
  }

  // "approve" / "deploy" (deploy is just approve from stage 6)
  const nextId = nextStageId(request.stage, request.risk);
  if (nextId == null) {
    return { request, audit: null };
  }
  const reachedEnd = nextId === 7;
  const next = {
    ...request,
    stage: nextId,
    status: reachedEnd ? "deployed" : "in-progress",
    // Stage 7 is the deployed terminal state — mirror the symmetry with
    // "reject" so terminal-filter predicates remain consistent.
    terminal: reachedEnd ? true : request.terminal,
    terminalReason: reachedEnd ? "deployed" : request.terminalReason,
    updatedAt: ts,
    decisions: [
      ...(request.decisions || []),
      { ts, actor: commonAudit.actor, decision: "approved", stage: request.stage },
    ],
  };
  const action = reachedEnd ? "deployment.completed" : `deployment.advanced.stage${nextId}`;
  return { request: next, audit: { ...commonAudit, action } };
}

/**
 * Returns the timeline view-model used by the detail panel — every stage
 * on the path tagged with state (complete | active | pending | skipped).
 */
export function buildTimeline(request) {
  const path = pathFor(request.risk);
  return STAGES.map((s) => {
    const onPath = path.includes(s.id);
    if (!onPath) return { ...s, state: "skipped" };
    if (request.terminal && request.status === "rejected") {
      return {
        ...s,
        state:
          s.id < request.stage ? "complete" : s.id === request.stage ? "rejected" : "skipped",
      };
    }
    if (s.id < request.stage) return { ...s, state: "complete" };
    if (s.id === request.stage) return { ...s, state: "active" };
    return { ...s, state: "pending" };
  });
}

/**
 * Approval checkpoints for stage 4. Shows progress when an actual decisions
 * list is present; in demo data, derives from request.stage progression.
 */
export function approvalChecklist(request) {
  const required = approversFor(request.risk);
  return required.map((approver, i) => {
    if (request.stage > 4) return { approver, state: "complete" };
    if (request.stage < 4) return { approver, state: "pending" };
    // stage === 4: derive from decisions (if any) else mark first as active
    const approvals = (request.decisions || []).filter((d) => d.decision === "approved" && d.stage === 4);
    if (i < approvals.length) return { approver, state: "complete" };
    if (i === approvals.length) return { approver, state: "active" };
    return { approver, state: "pending" };
  });
}

/** Stage label helper. */
export function stageLabel(id) {
  return stageById(id)?.label || "Unknown";
}

/**
 * In-flight = actively moving toward deployment, pre-approval.
 *
 * The PRD treats stage 5 (Approved) as exiting the In-flight queue —
 * those requests sit in the "Approved & queued" / Deployment Ready
 * buckets until they ship. So in-flight is strictly stages 1-4 that are
 * neither terminal nor rejected.
 */
export function isInFlight(request) {
  return request.stage < 5 && !isTerminal(request) && request.status !== "rejected";
}
